import { type ReactNode, useEffect, useRef } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type DialogProps = {
  children: ReactNode;
  description?: string;
  open: boolean;
  onClose: () => void;
  title: string;
  className?: string;
};

export const Dialog = ({
  children,
  description,
  open,
  onClose,
  title,
  className,
}: DialogProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `dialog-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      aria-labelledby={titleId}
      className="m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-visible bg-transparent p-0 backdrop:bg-neutral-950/50"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      ref={ref}
    >
      <div
        className={cn(
          "max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white p-5 text-neutral-950 shadow-xl ring-1 ring-neutral-950/10 sm:p-6",
          className
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-balance text-xl font-semibold" id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className="text-pretty pt-1 text-base text-neutral-600 sm:text-sm">
                {description}
              </p>
            ) : null}
          </div>
          <Button aria-label="Close dialog" onClick={onClose} size="icon" type="button" variant="ghost">
            <X className="size-4 shrink-0 stroke-current" />
          </Button>
        </div>
        <div className="pt-5">{children}</div>
      </div>
    </dialog>
  );
};
